import moment from "moment";
import { checkHasSameSelectedIngredients } from "../../../Account/useInvoiceModal/getOrderSummaryDates";
/**
 * Collects and combines recipes for a specific date from recipe assignments.
 * @param {Store} store - The store object containing the recipe model.
 * @param {function} getSelectedIngredientOptionTotal - Function to calculate total replacement prices for selected ingredients.
 * @param {Array<RecipeAssignment>} [recipeAssignments] - Array of recipe assignments.
 * @returns {Array<any>} - Combined array of recipes with their details.
 */
const getRecipesForDate = (store, getSelectedIngredientOptionTotal, recipeAssignments) => {
    // Return an empty array if no recipe assignments are provided
    if (!recipeAssignments)
        return [];
    // Initialize an array to hold combined recipes
    const combinedRecipes = [];
    // Iterate through each recipe assignment
    recipeAssignments.forEach((assignment) => {
        var _a, _b, _c, _d;
        // Check if the recipe has ingredients that must be replaced
        const hasMustReplaceIngredients = (_c = (_b = (_a = store.model.Recipes.find((r) => r.RecipeId === assignment.RecipeId)) === null || _a === void 0 ? void 0 : _a.RecipeIngredients) === null || _b === void 0 ? void 0 : _b.some((ri) => ri.MustReplace)) !== null && _c !== void 0 ? _c : false;
        // Filter the selected ingredient options to include only those that must be replaced
        const selectedIngredientOptions = (_d = assignment.SelectedIngredientOptions) === null || _d === void 0 ? void 0 : _d.filter((i) => i.mustBeReplaced);
        // Calculate the total replacement price for selected ingredient options that must be replaced
        const replacementPrice = getSelectedIngredientOptionTotal(selectedIngredientOptions);
        // Check if a recipe with the same recipeId and selected options already exists in combinedRecipes
        const existingRecipeIndex = combinedRecipes.findIndex((combinedRecipe) => combinedRecipe.recipeId === assignment.RecipeId &&
            checkHasSameSelectedIngredients(combinedRecipe.selectedIngredientOptions, selectedIngredientOptions));
        // If a matching recipe is found, update its quantity and replacement price
        if (existingRecipeIndex > -1) {
            combinedRecipes[existingRecipeIndex].quantity += assignment.Quantity; // Increment quantity
            combinedRecipes[existingRecipeIndex].replacementPrice += replacementPrice; // Increment replacement price
        }
        else {
            // If no matching recipe exists, add it to the combinedRecipes array
            combinedRecipes.push({
                name: assignment.RecipeName,
                shortName: assignment.RecipeShortName,
                price: assignment.RecipePrice,
                quantity: assignment.Quantity,
                recipeId: assignment.RecipeId,
                assignmentId: assignment.AssignmentId,
                hasMustReplaceIngredients: hasMustReplaceIngredients,
                replacementPrice: replacementPrice,
                selectedIngredientOptions: selectedIngredientOptions,
            });
        }
    });
    // Return the combined array of recipes
    return combinedRecipes;
};
/**
 * Collects meals for a specific date and structures them with their associated recipes.
 * @param {Array<moment.Moment>} mealDates - Array of meal dates to process.
 * @param {Array<Meal>} cartMeals - Array of meals in the cart.
 * @param {Store} store - The store object containing the meal and recipe models.
 * @param {function} getSelectedIngredientOptionTotal - Function to calculate total replacement prices for selected ingredients.
 * @returns {Array<Object>} - Array of objects containing date and meals with recipes.
 */
export const getDatesForCart = (mealDates, cartMeals, cartProducts, store, getSelectedIngredientOptionTotal) => {
    // Map over each meal date to structure the output
    const datesForOrder = mealDates.map((mealDate) => {
        return {
            date: moment(mealDate).format("YYYY-MM-DD"),
            meals: cartMeals
                .filter((meal) => moment(meal.MealDate).isSame(mealDate, "day")) // Filter meals that match the current date
                .map((meal) => {
                var _a, _b, _c, _d;
                return {
                    name: (_a = meal.ProgramMealName) !== null && _a !== void 0 ? _a : "",
                    programMealId: (_b = meal.ProgramMealId) !== null && _b !== void 0 ? _b : 0,
                    quantity: (_c = meal.Quantity) !== null && _c !== void 0 ? _c : 0,
                    price: (_d = meal === null || meal === void 0 ? void 0 : meal.Price) !== null && _d !== void 0 ? _d : 0,
                    recipes: getRecipesForDate(store, getSelectedIngredientOptionTotal, meal.RecipeAssignments), // Get recipes for the current meal
                };
            }),
            products: cartProducts
                .filter((product) => moment(mealDate).day() === product.DayOfWeek)
                .map((product) => {
                var _a;
                return {
                    name: product.productName,
                    quantity: product.quantity,
                    id: product.productId,
                    price: product.productPrice,
                    sale: product.salePrice !== 0 ? product.salePrice : undefined,
                    dayOfWeek: (_a = product.DayOfWeek) !== null && _a !== void 0 ? _a : -1,
                    isOnShortWeek: product.isOnShortWeek,
                };
            }),
            isDayPastCutoff: false,
        };
    });
    // List of days that have meals
    const mealDateDays = new Set(mealDates.map((d) => moment(d).day()));
    // List to hold the product only days
    const missingProductsByDay = {};
    cartProducts
        // Skip products that are already on a meal day or that belong to a short week
        .filter((p) => !mealDateDays.has(p.DayOfWeek) && !p.isOnShortWeek)
        .forEach((product) => {
        var _a;
        if (!missingProductsByDay[product.DayOfWeek]) {
            missingProductsByDay[product.DayOfWeek] = [];
        }
        missingProductsByDay[product.DayOfWeek].push({
            name: product.productName,
            quantity: product.quantity,
            id: product.productId,
            price: product.productPrice,
            sale: product.salePrice !== 0 ? product.salePrice : undefined,
            dayOfWeek: (_a = product.DayOfWeek) !== null && _a !== void 0 ? _a : -1,
            isOnShortWeek: product.isOnShortWeek,
        });
    });
    // Create new entries for each product only day
    Object.keys(missingProductsByDay).forEach((dayKey) => {
        datesForOrder.push({
            date: moment(store.cart.startDate).day(dayKey).format("YYYY-MM-DD"),
            meals: [],
            products: missingProductsByDay[dayKey],
            isDayPastCutoff: false,
        });
    });
    return datesForOrder;
};
